import React from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import StageSection from './StageSection'
import RunProgress from './RunProgress'
import ThemeDistribution from './ThemeDistribution'
import ResponseList from './ResponseList'
import SampleSizePicker from './SampleSizePicker'
import AiAccessTooltip from '../system/AiAccessTooltip'
import { buildThemeDistribution, clampSampleSize, recommendedSampleSize } from '../../utils/themeAIDistribution'
import type { ThemeAIRun, ThemeAIRunRecordRow, ThemeAIRunResultsResponse, ThemeAITheme } from '../../types/gotypes'

export interface ThemeAIRecordsView {
  records: ThemeAIRunRecordRow[]
  total: number
  offset: number
  pageSize: number
  loading: boolean
  onPageChange: (offset: number) => void
}

export interface TestRunStageProps {
  poolSize: number
  sampleSize: number
  onSampleSizeChange: (size: number) => void
  frameThemes: ThemeAITheme[]
  /** The latest preview coded against the current data and code frame. */
  testRun?: ThemeAIRun
  results?: ThemeAIRunResultsResponse
  resultsLoading: boolean
  /** A preview is being created or is still coding. */
  testing: boolean
  error?: string
  records: ThemeAIRecordsView
  selectedThemeCode?: string
  onSelectTheme: (code: string | undefined) => void
  /** Starts a test; with a size, that size (a quick pick is one click). */
  onRunTest: (sampleSize?: number) => void
  /** Why testing is closed to this person (#1537 AI access, needs level 2), or undefined. Viewing a test stays open. */
  blockedReason?: string
  onRefineFrame: () => void
  /** "Looks good": back to Run to code the full set. */
  onContinue: () => void
  /** Leave the test, back to Run. */
  onBack: () => void
  /** The last test ran on responses the agent did not read (undefined when unknown). */
  freshSample?: boolean
}

export const responsesLabel = (count: number) => `${count.toLocaleString()} response${count === 1 ? '' : 's'}`

// Test the frame on a sample before paying for everything (spec #916 §5.6):
// how the sample splits across the themes, and the actual responses behind
// each share. It is a view inside Run, not a step of its own, and it is
// called "Test on a sample" (Matt, 23 Sep: "Actually is it review? It's
// test."). The two ways out are the two decisions a user makes here.
const TestRunStage: React.FC<TestRunStageProps> = ({
  poolSize,
  sampleSize,
  onSampleSizeChange,
  frameThemes,
  testRun,
  results,
  resultsLoading,
  testing,
  error,
  records,
  selectedThemeCode,
  onSelectTheme,
  onRunTest,
  onRefineFrame,
  onContinue,
  onBack,
  freshSample,
  blockedReason,
}) => {
  const hasResults = Boolean(results && testRun?.status === 'completed')
  const distribution = results ? buildThemeDistribution(results.themes, frameThemes) : []
  const selectedTheme = distribution.find((item) => item.code === selectedThemeCode)
  const unused = distribution.filter((item) => item.count === 0).length
  const size = clampSampleSize(sampleSize, poolSize)

  return (
    <StageSection
      title="Test on a sample"
      description={
        hasResults
          ? `How ${(results?.total_records ?? size).toLocaleString()} sampled responses split across the themes.`
          : `Code a random sample before all ${poolSize.toLocaleString()} responses. It uses responses the agent did not read, so you see how the themes split before paying for everything.`
      }
      actions={<Button color="info" startIcon={<ArrowBackIcon />} onClick={onBack}>Run</Button>}
    >
      {error && <Alert severity="error">{error}</Alert>}
      {testRun?.status === 'failed' && !testing && (
        <Alert severity="error">
          The last test failed{testRun.error ? `: ${testRun.error}` : '.'} Try again, or refine the code frame.
        </Alert>
      )}

      {testing && (
        <Box sx={{ py: 2 }}>
          <RunProgress
            run={testRun}
            label={`Coding ${(testRun?.response_count || size).toLocaleString()} sampled responses against ${frameThemes.length} themes…`}
          />
        </Box>
      )}

      {!testing && !hasResults && !resultsLoading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle2">How many responses should the test code?</Typography>
          <SampleSizePicker
            poolSize={poolSize}
            value={size}
            onChange={onSampleSizeChange}
            onQuickPick={onRunTest}
            disabled={frameThemes.length === 0 || !!blockedReason}
          />
          <Typography variant="caption" color="text.secondary">
            {recommendedSampleSize(poolSize).toLocaleString()} is recommended for {poolSize.toLocaleString()} responses. A quick pick starts the test straight away. Nothing is written to your data.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <AiAccessTooltip reason={blockedReason}>
              <Button variant="contained" color="info" disabled={frameThemes.length === 0 || !!blockedReason} onClick={() => onRunTest(size)}>
                Test {responsesLabel(size)}
              </Button>
            </AiAccessTooltip>
            <Button color="info" onClick={onBack}>Skip the test</Button>
          </Box>
        </Box>
      )}

      {!testing && hasResults && results && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box sx={{ flex: '1 1 420px' }}>
              <SampleSizePicker poolSize={poolSize} value={size} onChange={onSampleSizeChange} />
            </Box>
            <AiAccessTooltip reason={blockedReason}>
              <Button variant="outlined" color="info" onClick={() => onRunTest(size)} disabled={frameThemes.length === 0 || !!blockedReason}>
                Test {responsesLabel(size)}
              </Button>
            </AiAccessTooltip>
          </Box>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: 'minmax(360px, 5fr) 7fr' },
              gap: 4,
              alignItems: 'start',
            }}
          >
            <Box sx={{ position: { lg: 'sticky' }, top: { lg: 16 } }}>
              <ThemeDistribution
                items={distribution}
                totalResponses={results.total_records}
                selectedCode={selectedThemeCode}
                onSelectTheme={onSelectTheme}
              />
              {freshSample !== undefined && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, px: 1 }}>
                  {freshSample
                    ? 'Tested on responses the agent did not read when it proposed the themes.'
                    : 'There are too few responses for a fully fresh sample, so the test reuses some the agent read.'}
                </Typography>
              )}
              {unused > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, px: 1 }}>
                  {unused === 1 ? 'One theme was' : `${unused} themes were`} not used in this sample — consider merging or removing {unused === 1 ? 'it' : 'them'}.
                </Typography>
              )}
            </Box>
            <ResponseList
              records={records.records}
              total={records.total}
              offset={records.offset}
              pageSize={records.pageSize}
              loading={records.loading}
              selectedCode={selectedThemeCode}
              selectedThemeName={selectedTheme?.name}
              onSelectTheme={onSelectTheme}
              onPageChange={records.onPageChange}
            />
          </Box>

          {/* Stays on screen while the themes and responses scroll, so the
              next step is always one click away (Adam, 23 Sep). */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.default',
            }}
          >
            <Button variant="contained" color="info" onClick={onContinue}>
              Looks good — run the full set
            </Button>
            <Button color="info" onClick={onRefineFrame}>
              Refine the code frame
            </Button>
          </Box>
        </>
      )}
    </StageSection>
  )
}

export default TestRunStage
